// @todo: Migrate to @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button as WeaveButton} from '@wandb/weave/components';
import {Tailwind} from '@wandb/weave/components/Tailwind';
import {isEmpty, isEqual} from 'lodash';
import React, {useEffect, useState} from 'react';
import {useDispatch} from 'react-redux';
import {createSelector} from 'reselect';
// eslint-disable-next-line wandb/no-deprecated-imports
import {Modal} from 'semantic-ui-react';

import {logHandledError} from '../../../services/errors/errorReporting';
import {useMemoizedSelector} from '../../../state/hooks';
import * as ViewActions from '../../../state/views/actions';
import * as ViewHooks from '../../../state/views/hooks';
import type * as PanelViewTypes from '../../../state/views/panel/types';
import {applyCopiedPanelConfig} from '../../../state/views/panelBankSectionConfig/actions';
import {type RootState} from '../../../types/redux';
import {type RunHistoryKeyInfo} from '../../../types/run';
import {haveDataForVega2Panel} from '../../../util/panelbank';
import type {PanelConfig, PanelSpec} from '../../../util/panelTypes';
import {useKeyInfoQueryContext} from '../../MultiRunWorkspace/KeyInfoQueryContext';
import {PanelCompRedux} from '../../PanelCompRedux';
import {InstrumentedCenteredWaveLoader as Loader} from '../../utility/InstrumentedLoader';
import {useMaybePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {usePanelContext} from '../PanelContextProvider';
import {SinglePanelInspectorContextProvider} from './SinglePanelInspectorContainer';

export interface PanelEditorProps {
  currentHeight: number;
  disableRunLinks?: boolean;
  // when we first open up the panel editor, should we start in edit mode?
  initialEditingState: boolean;
  initialConfigState?: {[key: string]: any};
  newPanel?: boolean;
  panelRef: PanelViewTypes.Ref;
  panelSpec: PanelSpec<any, any, any>;
  readOnly?: boolean;
  onCancel?(): void;
  onOK?(): void;
  onRemove?(): void;
}

export interface LoadedKeyInfoQuery {
  loading: false;
  error: null | undefined;
  historyKeyInfo: RunHistoryKeyInfo;
}

function isLoadedKeyInfoQuery(object: any): object is LoadedKeyInfoQuery {
  return !object.loading && object.error == null && 'historyKeyInfo' in object;
}

type ConfigRecord = Record<string, unknown>;

function configValueIsDefined(value: unknown) {
  return value !== undefined;
}

function panelConfigsDifferIgnoringUndefined(
  currentConfig: PanelConfig | undefined,
  initialConfig: PanelConfig | undefined
) {
  const current = (currentConfig ?? {}) as ConfigRecord;
  const initial = (initialConfig ?? {}) as ConfigRecord;
  const checkedKeys = new Set<string>();

  for (const [key, currentValue] of Object.entries(current)) {
    if (!configValueIsDefined(currentValue)) {
      continue;
    }
    checkedKeys.add(key);
    const initialValue = initial[key];
    if (
      !configValueIsDefined(initialValue) ||
      (currentValue !== initialValue && !isEqual(currentValue, initialValue))
    ) {
      return true;
    }
  }

  for (const [key, initialValue] of Object.entries(initial)) {
    if (!configValueIsDefined(initialValue) || checkedKeys.has(key)) {
      continue;
    }
    const currentValue = current[key];
    if (
      !configValueIsDefined(currentValue) ||
      (currentValue !== initialValue && !isEqual(currentValue, initialValue))
    ) {
      return true;
    }
  }

  return false;
}

const makePanelEditorPreviewSelector = (
  panelRef: PanelViewTypes.Ref,
  initialConfig?: PanelConfig
) =>
  createSelector(
    (state: RootState) => state.views.parts[panelRef.type]?.[panelRef.id],
    panel => {
      const config = panel?.config as any;

      return {
        hasConfigChanges:
          panel == null
            ? false
            : panelConfigsDifferIgnoringUndefined(config, initialConfig),
        isParallelCoordinatesPlot:
          panel?.viewType === 'Parallel Coordinates Plot',
        isRunHistoryLinePlot: panel?.viewType === 'Run History Line Plot',
        isScatterPlot: panel?.viewType === 'Scatter Plot',
        isVega2: panel?.viewType === 'Vega2',
        parallelCoordinatesColumnCount: config?.columns?.length ?? 0,
        runHistoryMetricsEmpty: isEmpty(config?.metrics),
        scatterXAxisEmpty: isEmpty(config?.xAxis),
        scatterYAxisEmpty: isEmpty(config?.yAxis),
        vega2UserQuery: config?.userQuery,
      };
    }
  );

/**
 * This is not great! But there's no way to easily communicate between the body of the modal and the apply button, so this context provider provides a way to avoid drilling.
 */
type PanelModalActions = {
  runOnApply: (() => void) | null;
  setRunOnApply: (runOnApply: () => void) => void;
};
const PanelModalActionContext = React.createContext<PanelModalActions>({
  runOnApply: () => {},
  setRunOnApply: () => {},
});

const PanelModalActionsContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [runOnApply, setRunOnApply] =
    React.useState<PanelModalActions['runOnApply']>(null);

  const contextValue = React.useMemo(
    () => ({runOnApply, setRunOnApply}),
    [runOnApply]
  );

  return (
    <PanelModalActionContext.Provider value={contextValue}>
      {children}
    </PanelModalActionContext.Provider>
  );
};
export const usePanelActionsContext = () => {
  const panelModalActions = React.useContext(PanelModalActionContext);

  // this is intended for stuff like analytics, so this should fail silently if it fails
  if (!panelModalActions) {
    logHandledError('PanelModalActionsContextProvider not found');
  }

  const fns = React.useMemo(
    () => ({
      runOnApply: !panelModalActions ? () => {} : panelModalActions.runOnApply,
      setRunOnApply: !panelModalActions
        ? () => {}
        : panelModalActions.setRunOnApply,
    }),
    [panelModalActions]
  );

  return fns;
};

const PanelEditor = ({
  currentHeight,
  disableRunLinks,
  initialEditingState,
  initialConfigState,
  newPanel,
  panelRef,
  panelSpec,
  onCancel,
  onOK,
  onRemove,
  readOnly,
}: PanelEditorProps) => {
  const dispatch = useDispatch();
  const panelCopy = ViewHooks.useObjectCopy(panelRef);
  const {keyInfoQuery} = useKeyInfoQueryContext();
  const {panelBankSectionConfigRef} = usePanelContext();
  // Falls back to undefined outside PanelsSharedRefsContext, which causes
  // applyCopiedPanelConfig to use the legacy (non-sparse) path. This is intentional.
  const panelBankConfigRef =
    useMaybePanelsSharedRefsContext()?.panelBankConfigRef;

  const panelActions = usePanelActionsContext();
  const [isEditMode, setIsEditMode] = useState(initialEditingState);

  // Disable the "Ok" button if we don't have enough info to render the plot
  // Keep this selector narrow. usePart here subscribes to the whole copied
  // panel config, which makes small dropdown edits re-render on unrelated data.
  const panelPreview = useMemoizedSelector(
    makePanelEditorPreviewSelector,
    panelCopy.copying ? panelRef : panelCopy.ref,
    initialConfigState?.config
  );
  const okDisabled =
    (panelPreview.isRunHistoryLinePlot &&
      panelPreview.runHistoryMetricsEmpty) ||
    (panelPreview.isScatterPlot &&
      (panelPreview.scatterXAxisEmpty || panelPreview.scatterYAxisEmpty)) ||
    (panelPreview.isParallelCoordinatesPlot &&
      panelPreview.parallelCoordinatesColumnCount < 2) ||
    (panelPreview.isVega2 &&
      !(
        isLoadedKeyInfoQuery(keyInfoQuery) &&
        haveDataForVega2Panel(
          keyInfoQuery.historyKeyInfo,
          panelPreview.vega2UserQuery
        )
      ));

  useEffect(() => {
    const callback = (event: KeyboardEvent) => {
      // event.metaKey - pressed Command key on Macs
      // event.ctrlKey - pressed Control key on Linux or Windows
      if ((event.metaKey || event.ctrlKey) && event.code === 'Enter') {
        event.preventDefault();
        if (onOK) {
          onOK();
        }
      }
    };
    document.addEventListener('keydown', callback);
    return () => {
      document.removeEventListener('keydown', callback);
    };
  }, [onOK]);

  return (
    <Modal
      className={[
        panelSpec.type.toLowerCase().replace(' ', '-') + '-panel-editor',
        isEditMode ? 'edit-mode' : 'fullscreen-mode',
        'panel-editor',
      ].join(' ')}
      open={true}
      onClick={(e: React.SyntheticEvent) => {
        e.stopPropagation();
      }}
      onClose={e => {
        e.stopPropagation(); // prevents exiting full screen panel on ESC key press
        onCancel?.();
      }}
      size="fullscreen">
      <Modal.Content className={isEditMode ? 'edit-mode' : 'fullscreen-mode'}>
        {panelCopy.copying ||
        keyInfoQuery.loading ||
        keyInfoQuery.error != null ? (
          <div style={{height: 500}}>
            <Loader name="key-info-query" size="huge" />
          </div>
        ) : (
          <PanelCompRedux
            configMode={isEditMode}
            currentHeight={currentHeight}
            dimensions={undefined as any}
            disableRunLinks={disableRunLinks}
            initialConfigState={initialConfigState}
            keyInfo={keyInfoQuery.historyKeyInfo}
            panelRef={panelSpec.type === 'Weave' ? panelRef : panelCopy.ref}
          />
        )}
      </Modal.Content>
      <Modal.Actions>
        <Tailwind>
          <div className="flex items-center justify-between">
            <div>
              {!panelSpec.noEditMode && !readOnly && !isEditMode && (
                <WeaveButton
                  onClick={() => setIsEditMode(true)}
                  size="large"
                  variant="secondary">
                  Edit
                </WeaveButton>
              )}
              {isEditMode && onRemove && (
                <WeaveButton
                  className="order ml-12"
                  onClick={onRemove}
                  size="large"
                  variant="secondary">
                  Delete
                </WeaveButton>
              )}
            </div>
            <div>
              {onCancel && (
                <WeaveButton
                  data-test="full-screen-modal-close-button"
                  onClick={onCancel}
                  size="large"
                  variant="secondary">
                  {isEditMode ? 'Cancel' : 'Close'}
                </WeaveButton>
              )}
              {isEditMode && onOK && (
                <WeaveButton
                  className="ml-12"
                  data-test="submit-panel-config"
                  disabled={panelCopy.copying || okDisabled}
                  onClick={() => {
                    if (!panelPreview.hasConfigChanges && onCancel) {
                      onCancel();
                      return;
                    }

                    panelActions.runOnApply?.();
                    if (!panelCopy.copying) {
                      if (newPanel) {
                        dispatch(
                          ViewActions.copyObject(panelCopy.ref, panelRef)
                        );
                      } else if (panelBankSectionConfigRef) {
                        dispatch(
                          applyCopiedPanelConfig(
                            panelBankSectionConfigRef,
                            panelCopy.ref,
                            panelRef,
                            panelBankConfigRef
                          )
                        );
                        window.analytics?.track('Panel added', {
                          type: panelSpec.type,
                        });
                      }
                      if (onOK) {
                        onOK();
                      }
                    }
                  }}
                  size="large"
                  variant="primary">
                  Apply
                </WeaveButton>
              )}
            </div>
          </div>
        </Tailwind>
      </Modal.Actions>
    </Modal>
  );
};

const WrappedPanelEditor = React.memo((props: PanelEditorProps) => (
  <SinglePanelInspectorContextProvider>
    <PanelModalActionsContextProvider>
      <PanelEditor {...props} />
    </PanelModalActionsContextProvider>
  </SinglePanelInspectorContextProvider>
));
WrappedPanelEditor.displayName = 'WrappedPanelEditor';

export default WrappedPanelEditor;
